import { Fragment, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import StatusBadge from "../components/StatusBadge";
import { gbp, dateShort, dateTimeShort } from "../lib/format";

export default function ApprovalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const stage = (search.get("stage") ?? "manager") as "manager" | "payroll";
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data: claim, isLoading, error } = useQuery({
    queryKey: ["claim", id],
    queryFn: () => api.expenses.get(id!),
    enabled: !!id,
  });
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: api.me });

  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [lineFlags, setLineFlags] = useState<Record<string, string>>({});
  const [showRevert, setShowRevert] = useState(false);
  const [revertTo, setRevertTo] = useState<"draft" | "submitted" | "manager_approved" | "payroll_approved">("payroll_approved");
  const [revertReason, setRevertReason] = useState("");

  const approve = useMutation({
    mutationFn: () => api.approvals.approve(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["claim", id] });
      qc.invalidateQueries({ queryKey: ["approvals"] });
      nav(`/approvals?stage=${stage}`);
    },
  });

  const reject = useMutation({
    mutationFn: () =>
      api.approvals.reject(
        id!,
        rejectReason,
        Object.entries(lineFlags).map(([line_id, reason]) => ({ line_id, reason: reason.trim() || "Receipt does not match or is missing" })),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["claim", id] });
      qc.invalidateQueries({ queryKey: ["approvals"] });
      nav(`/approvals?stage=${stage}`);
    },
  });

  const markPaid = useMutation({
    mutationFn: () => api.approvals.markPaid(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claim", id] }),
  });

  const revert = useMutation({
    mutationFn: () => api.approvals.revert(id!, revertTo, revertReason.trim()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["claim", id] });
      qc.invalidateQueries({ queryKey: ["approvals"] });
      qc.invalidateQueries({ queryKey: ["admin-all-claims"] });
      setShowRevert(false);
      setRevertReason("");
    },
  });

  if (isLoading) return <div className="text-slate-500">Loading…</div>;
  if (error) return <div className="text-red-600 text-sm">{(error as Error).message}</div>;
  if (!claim) return <div className="text-slate-500">Not found</div>;

  const canActManager = claim.status === "submitted";
  const canActPayroll = claim.status === "manager_approved" && claim.payroll_required;
  const canMarkPaid = claim.status === "payroll_approved";

  // Revert availability
  const myRoles = me?.roles ?? [];
  const isAdmin = myRoles.includes("Admin");
  const isPayroll = myRoles.includes("Payroll");
  const lifecyclePos: Record<string, number> = { draft: 0, submitted: 1, manager_approved: 2, payroll_approved: 3, paid: 4 };
  const currentPos = lifecyclePos[claim.status] ?? -1;
  const canRevert =
    currentPos > 0 &&
    claim.status !== "rejected" &&
    (isAdmin || (isPayroll && claim.status === "paid"));
  const revertOptions: { value: "draft" | "submitted" | "manager_approved" | "payroll_approved"; label: string }[] = [];
  if (canRevert) {
    if (isAdmin) {
      if (currentPos > 0) revertOptions.push({ value: "draft", label: "Back to draft (claimant edits)" });
      if (currentPos > 1) revertOptions.push({ value: "submitted", label: "Back to submitted (manager redo)" });
      if (currentPos > 2) revertOptions.push({ value: "manager_approved", label: "Back to manager approved (payroll redo)" });
      if (currentPos > 3) revertOptions.push({ value: "payroll_approved", label: "Back to payroll approved (mark paid redo)" });
    } else if (isPayroll && claim.status === "paid") {
      revertOptions.push({ value: "payroll_approved", label: "Back to payroll approved (re-issue payment)" });
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between no-print">
        <Link to={`/approvals?stage=${stage}`} className="text-sm text-slate-500 hover:text-slate-700">
          ← Back to {stage} queue
        </Link>
        <button
          onClick={() => window.print()}
          className="text-sm text-slate-500 hover:text-slate-700"
        >
          Save as PDF / Print
        </button>
      </div>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-liaise-navy">{claim.request_number}</h1>
          <div className="text-slate-600 mt-2 flex items-center gap-3 text-sm">
            <StatusBadge status={claim.status} />
            <span>{claim.cost_centre_name}</span>
          </div>
          <div className="mt-3 text-sm">
            <div className="text-slate-500">Claimant</div>
            {(claim as any).claimant_entra_oid ? (
              <Link
                to={`/admin/claimant/${(claim as any).claimant_entra_oid}`}
                className="font-medium text-slate-700 hover:text-liaise-navy hover:underline"
              >
                {claim.claimant_display_name}
              </Link>
            ) : (
              <div className="font-medium text-slate-700">{claim.claimant_display_name}</div>
            )}
            <div className="text-slate-500 text-xs">{claim.claimant_email}</div>
          </div>
          {claim.manager_overridden && (
            <div className="mt-2 text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded inline-block">
              Manager overridden by claimant: {claim.manager_override_reason}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-sm text-slate-500">Total</div>
          <div className="text-3xl font-semibold text-liaise-navy">{gbp(Number(claim.total_amount))}</div>
          {Number(claim.total_vat) > 0 && (
            <div className="text-sm text-slate-500">incl. VAT {gbp(Number(claim.total_vat))}</div>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="font-medium text-liaise-navy">Lines ({claim.lines.length})</h2>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="px-6 py-3 font-medium">Date</th>
              <th className="px-6 py-3 font-medium">Category</th>
              <th className="px-6 py-3 font-medium">Description</th>
              <th className="px-6 py-3 font-medium">Merchant</th>
              <th className="px-6 py-3 font-medium text-right">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {claim.lines.map((l) => {
              const lineAttachments = claim.attachments.filter((a) => a.line_id === l.id);
              return (
                <Fragment key={l.id}>
                  <tr>
                    <td className="px-6 py-3 text-slate-600">{dateShort(l.expense_date)}</td>
                    <td className="px-6 py-3">{l.category_name}</td>
                    <td className="px-6 py-3 text-slate-600">{l.description ?? <span className="text-slate-400 italic">—</span>}</td>
                    <td className="px-6 py-3 text-slate-600">{l.merchant_name ?? <span className="text-slate-400 italic">—</span>}</td>
                    <td className="px-6 py-3 text-right font-medium">{gbp(Number(l.amount))}</td>
                  </tr>
                  {(lineAttachments.length > 0 || l.is_mileage) && (
                    <tr>
                      <td colSpan={5} className="px-6 pb-4 -mt-1">
                        <div className="flex flex-wrap items-center gap-2">
                          {l.is_mileage && (
                            <span className="text-xs text-slate-600 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full">
                              🚗 {l.mileage_miles ?? 0} miles
                              {(l.mileage_origin_home || l.mileage_origin_postcode) &&
                                ` · ${l.mileage_origin_home || l.mileage_origin_postcode} → ${l.mileage_destination_home || l.mileage_destination_postcode || "?"}`}
                              {l.mileage_vehicle_type && ` · ${l.mileage_vehicle_type.replace("_", " ")}`}
                              {l.mileage_rate_pence != null && ` · ${l.mileage_rate_pence}p/mile`}
                            </span>
                          )}
                          {lineAttachments.map((a) => (
                            <button
                              key={a.id}
                              onClick={async () => {
                                const r = await api.expenses.attachmentDownloadUrl(claim.id, a.id);
                                window.open(r.url, "_blank", "noopener,noreferrer");
                              }}
                              className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full transition ${
                                a.is_declaration
                                  ? "bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100"
                                  : "bg-liaise-navy-50 text-liaise-navy hover:bg-liaise-navy-200"
                              }`}
                              title={a.is_declaration ? "No receipt — claimant signed the Appendix 1 missing receipt declaration" : undefined}
                            >
                              {a.is_declaration ? "📝" : a.content_type.startsWith("image/") ? "🖼️" : "📄"} {a.file_name}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Action panel */}
      {(canActManager || canActPayroll || canMarkPaid) && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm no-print">
          {!showReject ? (
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-600">
                {canActManager && stage === "manager" && "Review the lines and approve, or reject with feedback."}
                {canActPayroll && stage === "payroll" && "Manager has approved. Approve to release for payment."}
                {canMarkPaid && "Approved for payment. Mark paid once the BACS run is complete."}
              </div>
              <div className="flex gap-2">
                {(canActManager || canActPayroll) && (
                  <>
                    <button
                      onClick={() => setShowReject(true)}
                      className="bg-white border border-red-300 text-red-700 font-medium px-5 py-2.5 rounded-lg hover:bg-red-50"
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => approve.mutate()}
                      disabled={approve.isPending}
                      className="bg-liaise-navy text-white font-medium px-5 py-2.5 rounded-lg hover:bg-liaise-navy-700 disabled:opacity-50"
                    >
                      {approve.isPending ? "Approving…" : "Approve"}
                    </button>
                  </>
                )}
                {canMarkPaid && (
                  <button
                    onClick={() => markPaid.mutate()}
                    disabled={markPaid.isPending}
                    className="bg-emerald-600 text-white font-medium px-5 py-2.5 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {markPaid.isPending ? "Marking…" : "Mark paid"}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-slate-700">Reason for rejection (will be sent to {claim.claimant_display_name})</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                placeholder="e.g. Receipt not attached for line 2 — please add and resubmit."
                className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:border-liaise-navy focus:ring-1 focus:ring-liaise-navy outline-none"
              />
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                <div className="px-3 py-2 text-xs font-medium text-slate-500">
                  Flag specific lines with a receipt problem (optional). The claimant sees the note against the highlighted line.
                </div>
                {claim.lines.map((l) => {
                  const on = l.id in lineFlags;
                  return (
                    <div key={l.id} className="px-3 py-2">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={(e) =>
                            setLineFlags((s) => {
                              const n = { ...s };
                              if (e.target.checked) n[l.id] = "";
                              else delete n[l.id];
                              return n;
                            })
                          }
                        />
                        <span className="text-slate-700">
                          {dateShort(l.expense_date)} · {l.category_name} · {gbp(Number(l.amount))}
                        </span>
                      </label>
                      {on && (
                        <input
                          type="text"
                          value={lineFlags[l.id]}
                          onChange={(e) => setLineFlags((s) => ({ ...s, [l.id]: e.target.value }))}
                          placeholder="What is wrong with this receipt?"
                          className="mt-2 w-full border border-slate-300 rounded px-2 py-1 text-sm"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => reject.mutate()}
                  disabled={!rejectReason.trim() || reject.isPending}
                  className="bg-red-600 text-white font-medium px-5 py-2.5 rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {reject.isPending ? "Rejecting…" : "Confirm reject"}
                </button>
                <button onClick={() => { setShowReject(false); setRejectReason(""); setLineFlags({}); }} className="text-slate-600 px-4 py-2 hover:underline">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Revert panel — admin / payroll only, status-aware */}
      {canRevert && revertOptions.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm no-print">
          {!showRevert ? (
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-600">
                <span className="font-medium text-slate-700">Need to roll this back?</span>{" "}
                Use revert to send the claim back to an earlier stage. Audited and emailed to the claimant.
              </div>
              <button
                onClick={() => {
                  setShowRevert(true);
                  setRevertTo(revertOptions[0].value);
                }}
                className="bg-white border border-amber-300 text-amber-800 font-medium px-4 py-2 rounded-lg hover:bg-amber-50"
              >
                Revert…
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-baseline justify-between">
                <h3 className="font-medium text-amber-800">Revert claim</h3>
                <span className="text-xs text-slate-500">From <strong>{claim.status}</strong></span>
              </div>
              <label className="block text-sm">
                <span className="block font-medium text-slate-700 mb-1">Send back to</span>
                <select
                  value={revertTo}
                  onChange={(e) => setRevertTo(e.target.value as any)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                >
                  {revertOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="block font-medium text-slate-700 mb-1">Reason (will be emailed to {claim.claimant_display_name})</span>
                <textarea
                  value={revertReason}
                  onChange={(e) => setRevertReason(e.target.value)}
                  rows={3}
                  placeholder="e.g. Bank details on file are incorrect, payment failed. Please confirm sort code/account number with HR."
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:border-liaise-navy focus:ring-1 focus:ring-liaise-navy outline-none"
                />
              </label>
              {revert.error && (
                <div className="text-red-600 text-sm">{(revert.error as Error).message}</div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => revert.mutate()}
                  disabled={!revertReason.trim() || revert.isPending}
                  className="bg-amber-600 text-white font-medium px-5 py-2.5 rounded-lg hover:bg-amber-700 disabled:opacity-50"
                >
                  {revert.isPending ? "Reverting…" : "Confirm revert"}
                </button>
                <button onClick={() => { setShowRevert(false); setRevertReason(""); }} className="text-slate-600 px-4 py-2 hover:underline">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {(claim.approvals.length > 0 || (claim as any).manager_overridden) && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h2 className="font-medium text-liaise-navy mb-4">Activity</h2>
          <ul className="space-y-2 text-sm">
            {(claim as any).manager_overridden && (
              <li className="flex justify-between text-slate-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <span>
                  <span className="font-medium text-amber-800">Manager overridden</span>
                  {" by "}
                  <span className="text-slate-700">{(claim as any).manager_override_set_by_email ?? "unknown"}</span>
                  {(claim as any).manager_override_reason && (
                    <span className="block text-xs italic text-slate-600 mt-1">"{(claim as any).manager_override_reason}"</span>
                  )}
                </span>
                {(claim as any).manager_override_set_at && (
                  <span className="text-xs text-slate-500">{dateTimeShort((claim as any).manager_override_set_at)}</span>
                )}
              </li>
            )}
            {claim.approvals.map((a: any, i: number) => (
              <li key={i} className="flex justify-between text-slate-600">
                <span>
                  <span className="font-medium text-slate-700 capitalize">{a.action}</span>
                  {" by "}
                  <span className="text-slate-700">{a.actor_email}</span>
                  <span className="text-xs ml-2 text-slate-500">({a.stage})</span>
                  {a.reason && <span className="block text-xs italic text-slate-500">"{a.reason}"</span>}
                </span>
                <span className="text-xs text-slate-500">{dateTimeShort(a.acted_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
